import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  CardBody, 
  CardHeader, 
  Table, 
  Button, 
  Modal, 
  ModalHeader, 
  ModalBody, 
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert
} from 'reactstrap';
import { authenticatedFetch } from '../utils/apiUtils';
import userManager from '../auth/oidcConfig';
import { jwtDecode } from 'jwt-decode';

const TransferRepair = () => {
  const [problemTransfers, setProblemTransfers] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentAsset, setCurrentAsset] = useState(null);
  const [password, setPassword] = useState('');
  const [repairResult, setRepairResult] = useState(null);
  const [requiresPassword, setRequiresPassword] = useState(false);

  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const user = await userManager.getUser();
        if (user && user.access_token) {
          const decodedToken = jwtDecode(user.access_token);
          const roles = decodedToken.role || [];
          const isAdminUser = Array.isArray(roles)
            ? roles.includes("Admin")
            : roles === "Admin";

          setIsAdmin(isAdminUser);
          
          if (!isAdminUser) {
            // Redirect non-admin users
            window.location.href = '/';
            return;
          }
          
          // Load problem transfers
          loadProblemTransfers();
        } else {
          setIsAdmin(false);
          setLoading(false);
          setError('Not authenticated');
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
        setLoading(false);
        setError('Authentication error');
      }
    };

    checkAdminStatus();
  }, []);

  const loadProblemTransfers = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch('/api/admin/problem-transfers');
      
      if (response.ok) {
        const data = await response.json();
        setProblemTransfers(data);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to load problem transfers');
      }
    } catch (error) {
      console.error('Error loading problem transfers:', error);
      setError('Failed to load problem transfers: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRepairClick = (asset) => {
    setCurrentAsset(asset);
    
    // Standard security level doesn't need password
    if (asset.userSecurityLevel === 'Standard') {
      setRequiresPassword(false);
      repairTransfer(asset.assetId, asset.userId, '');
    } else {
      setRequiresPassword(true);
      setShowPasswordModal(true);
    }
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    repairTransfer(currentAsset.assetId, currentAsset.userId, password);
    setShowPasswordModal(false);
    setPassword('');
  };

  const repairTransfer = async (assetId, userId, password) => {
    try {
      setLoading(true);
      setRepairResult(null);
      
      const response = await authenticatedFetch('/api/admin/repair-transfer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          assetId,
          userId,
          password
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setRepairResult({
          success: true,
          message: result.message || 'Asset repaired successfully'
        });
        
        // Refresh the list
        loadProblemTransfers();
      } else {
        const errorData = await response.json();
        setRepairResult({
          success: false,
          message: errorData.message || 'Failed to repair asset'
        });
      }
    } catch (error) {
      console.error('Error repairing transfer:', error);
      setRepairResult({
        success: false,
        message: 'Error repairing transfer: ' + error.message
      });
    } finally {
      setLoading(false);
    }
  };

  // If not admin, show access denied or redirect
  if (!isAdmin && !loading) {
    return (
      <Container>
        <Alert color="danger">
          Access denied. This page is only available to administrators.
        </Alert>
      </Container>
    );
  }

  return (
    <Container>
      <Row className="mb-4">
        <Col>
          <h1>Transfer Repair Admin Panel</h1>
          <p className="text-muted">
            This tool allows administrators to repair broken asset transfers where the encryption failed.
          </p>
        </Col>
      </Row>

      {repairResult && (
        <Row className="mb-4">
          <Col>
            <Alert color={repairResult.success ? 'success' : 'danger'}>
              {repairResult.message}
            </Alert>
          </Col>
        </Row>
      )}

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert color="danger">
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          <Card>
            <CardHeader className="bg-primary text-white">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="m-0">Problem Transfers</h5>
                <Button 
                  color="light" 
                  size="sm" 
                  onClick={loadProblemTransfers} 
                  disabled={loading}
                >
                  <i className="fas fa-sync-alt me-1"></i>
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              {loading ? (
                <div className="text-center my-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading transfers...</p>
                </div>
              ) : problemTransfers.length === 0 ? (
                <Alert color="info">
                  No problem transfers found. All transfers appear to be working correctly.
                </Alert>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Asset ID</th>
                        <th>Asset Name</th>
                        <th>Transfer Date</th>
                        <th>Current Owner</th>
                        <th>Security Level</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {problemTransfers.map(asset => (
                        <tr key={asset.assetId}>
                          <td>{asset.assetId}</td>
                          <td>{asset.assetName || 'Unknown Item'}</td>
                          <td>{new Date(asset.transferDate).toLocaleString()}</td>
                          <td>{asset.ownerUsername}</td>
                          <td>
                            <span className={`badge bg-${asset.userSecurityLevel === 'Standard' ? 'success' : asset.userSecurityLevel === 'Enhanced' ? 'warning' : 'danger'}`}>
                              {asset.userSecurityLevel}
                            </span>
                          </td>
                          <td>
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => handleRepairClick(asset)}
                              disabled={loading}
                            >
                              <i className="fas fa-wrench me-1"></i>
                              Repair
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Password Modal for Enhanced/Maximum Security Users */}
      <Modal isOpen={showPasswordModal} toggle={() => setShowPasswordModal(false)}>
        <ModalHeader toggle={() => setShowPasswordModal(false)}>
          User Password Required
        </ModalHeader>
        <ModalBody>
          <p>
            This user has {currentAsset?.userSecurityLevel} security level enabled, which requires a password
            to decrypt their assets. Please enter the user's password to continue with the repair:
          </p>
          <Form onSubmit={handlePasswordSubmit}>
            <FormGroup>
              <Label for="password">User Password</Label>
              <Input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <small className="text-muted">
                The password is only used temporarily to generate the encryption key and is not stored.
              </small>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowPasswordModal(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handlePasswordSubmit} disabled={!password}>
            Repair Asset
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default TransferRepair;
